import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import MdnList from "./MDNList";
import Title from "../../common/Title";
import Search from "../../common/search/Search";
import CustomSelect from "../../common/CustomSelect";

import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import { STATUS_MAPPING } from "../../../constants/value";
import {
  __resetPort,
  __resetNetwork,
  __exportMdns,
  __getMdnsAll,
  __getMDNStatus,
  __getCarrierMDNStatusPost,
} from "../../../api/mdn";

const equalsIgnoreCase = (valueA, valueB) => {
  if (!valueA) return false;
  if (!valueB) return false;

  return String(valueA).toLowerCase() === String(valueB).toLowerCase();
};

const MdnFull = () => {
  const { user } = useAuth();
  const [mdns, setMdns] = useState([]);
  const [filteredMdns, setFilteredMdns] = useState([]);
  const [mdnStatus, setMdnStatus] = useState({});
  const [carrierStatus, setCarrierStatus] = useState({});
  const [filters, setFilters] = useState({});
  const [filterStatus, setFilterStatus] = useState(false);
  const [queryOptions, setQueryOptions] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 100,
    total: 1,
  });
  const [sortQuery, setSortQuery] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [response, call, loading] = useAxios({ ...__getMdnsAll });
  const [statusResponse, statusCall, statusLoading] = useAxios({
    ...__getMDNStatus,
  });
  const [carrierStatusResponse, carrierStatusCall, carrierStatusLoading] =
    useAxios({
      ...__getCarrierMDNStatusPost,
    });
  const [exportResponse, exportCall, exportLoading] = useAxios({
    ...__exportMdns,
  });
  const [resetResponse, resetCall, resetLoading] = useAxios({});
  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  const handleFilterChange = React.useCallback((filterModel) => {
    const filterOption = filterModel?.items.length ? filterModel.items[0] : {};

    setFilters({
      filter_column: filterOption.columnField,
      filter_operator: filterOption.operatorValue,
      filter_value: filterOption.value ? filterOption.value : "",
    });
  }, []);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page: currentPage,
        page_size: pageSize,
        search_query: searchValue,
        sort: sortQuery,
        ...queryOptions,
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
  }, [currentPage, pageSize, searchValue, sortQuery, queryOptions]);

  useEffect(() => {
    if (response && response.status) {
      setMetaData(response.data.meta);
      setMdns(response.data.data);
      if (response.data.data.length > 1 && user.id !== 1) {
        const gateway_ids = response.data.data.reduce((gateways, val) => {
          if (val.gateway_id) gateways.add(val.gateway_id);
          return gateways;
        }, new Set());

        const carrierMdns = response.data.data.reduce((carrierSet, val) => {
          if (!val.gateway_id && val.mdn) carrierSet.add(val.mdn);
          return carrierSet;
        }, new Set());

        if (gateway_ids.size)
          statusCall({ params: { ids: Array.from(gateway_ids.values()) } });

        const carrierMdnList = Array.from(carrierMdns.values()).filter(
          (mdn) => mdn !== null && mdn !== undefined
        );

        if (carrierMdnList.length) {
          carrierStatusCall({
            data: { mdns: carrierMdnList },
          });
        }
      } else if (response.data.data.length === 1) {
        if (response.data.data[0].gateway_id === 0 && response.data.data[0].mdn)
          carrierStatusCall({ data: { mdns: [response.data.data[0].mdn] } });
      }
    }
  }, [response]);

  useEffect(() => {
    if (user.id === 1 || !filterStatus) {
      setFilteredMdns(mdns);
      return;
    }

    const filterLabel = STATUS_MAPPING[filterStatus] || filterStatus;

    const getRowStatusLabel = (row) => {
      if (row.gateway_id === 0) {
        return carrierStatus[row.mdn] || STATUS_MAPPING.not_found;
      }

      if (
        mdnStatus &&
        row.gateway_id &&
        row.port &&
        mdnStatus[row.gateway_id] &&
        mdnStatus[row.gateway_id][row.port]
      ) {
        return mdnStatus[row.gateway_id][row.port];
      }

      return row.status || STATUS_MAPPING.not_found;
    };

    const filteredRecords = mdns.filter((row) =>
      equalsIgnoreCase(getRowStatusLabel(row), filterLabel)
    );

    setFilteredMdns(filteredRecords);
  }, [mdns, filterStatus, carrierStatus, mdnStatus, user.id]);

  useEffect(() => {
    if (statusResponse && statusResponse.data) {
      setMdnStatus(statusResponse.data);
      setMdns(mdns);
    }
  }, [statusResponse]);

  useEffect(() => {
    if (carrierStatusResponse && carrierStatusResponse.data) {
      setCarrierStatus(carrierStatusResponse.data);
      setMdns(mdns);
    }
  }, [carrierStatusResponse]);

  useEffect(() => {
    if (exportResponse && exportResponse.status) {
      fileDownload(exportResponse.data, "mdn_export.csv");
    }
  }, [exportResponse]);

  const handleFilterPanelClose = () => {
    setCurrentPage(1);
    setQueryOptions({ ...filters });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  const clearSearch = () => {
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleSearchChange = (searchString) => {
    setSearchValue(searchString);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Title variant="h5" content="MDN" gutterBottom />
      {response && (
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item>
            <Stack sx={{ mb: "5px" }}>
              <Search
                searchValue={searchValue}
                clearSearch={clearSearch}
                changeSearchValue={handleSearchChange}
              />
            </Stack>
          </Grid>
          {user.id !== 1 && (
            <Grid item>
              <CustomSelect
                isClearable={false}
                isSearchable={false}
                closeMenuOnSelect={true}
                options={[
                  { value: null, label: "All" },
                  ...Object.keys(STATUS_MAPPING).map((val) => ({
                    value: val,
                    label: STATUS_MAPPING[val],
                  })),
                ]}
                defaultValue={{ value: null, label: "All" }}
                handleChange={(val) => setFilterStatus(val.value)}
              />
            </Grid>
          )}
        </Grid>
      )}
      {response ? (
        <MdnList
          mdns={filteredMdns}
          loading={loading || resetLoading}
          carrierLoading={carrierStatusLoading}
          status={mdnStatus}
          carrierStatus={carrierStatus}
          statusLoading={statusLoading}
          meta={metaData}
          client={user.id !== 1}
          handleSort={setSortQuery}
          handleFilterChange={handleFilterChange}
          handlePageChange={handlePageChange}
          handleRefreshClick={() =>
            debouncedFetchData({
              params: {
                page: currentPage,
                page_size: pageSize,
                search_query: searchValue,
                sort: sortQuery,
                ...queryOptions,
              },
            })
          }
          handleResetCall={(id) => resetCall({ ...__resetPort(id) })}
          handleResetNetworkCall={(id) => resetCall({ ...__resetNetwork(id) })}
          handlePageSizeChange={handlePageSizeChange}
          onPreferencePanelClose={(params, events, details) => {
            handleFilterPanelClose();
          }}
        />
      ) : (
        <Title content="Please wait..." variant="subtitle2" gutterBottom />
      )}
    </Box>
  );
};

export default MdnFull;
